import React, { useState, useContext, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useConfirm } from 'material-ui-confirm'
import { enqueueSnackbar } from 'notistack'
import { Helmet } from 'react-helmet-async'
import { v4 as uuid } from 'uuid'
import Cropper from "react-cropper"
import "cropperjs/dist/cropper.css"

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Fab from '@mui/material/Fab'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem'

import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'

import SaveIcon from '@mui/icons-material/Save'
import TransformIcon from '@mui/icons-material/Transform'
import UndoIcon from '@mui/icons-material/Undo'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CasinoIcon from '@mui/icons-material/Casino'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'

import { ThemeContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

export default function WordDetailsPage() {
  const theme = useContext(ThemeContext)
  const confirm = useConfirm()
  const navigate = useNavigate()
  const location = useLocation()
  // Only poll for background-synthesized audio (#9) when we arrived straight
  // from creating this word; a normally-visited audio-less word never polls.
  const awaitAudio = Boolean(location.state && location.state.awaitAudio)
  let { wordId } = useParams()

  const [word, setWord] = useState(null)
  const [translation, setTranslation] = useState(null)

  const [audioUrl, setAudioUrl] = useState(null)
  const [audioPending, setAudioPending] = useState(false)
  const [voice, setVoice] = useState("")
  const [voices, setVoices] = useState([])
  const [mp3Loading, setMp3Loading] = useState(false)
  const [mp3, setMp3] = useState(null)
  const [syntTimeout, setSyntTimeout] = useState(300)
  const [elapsed, setElapsed] = useState(0)
  const [requestTime, setRequestTime] = useState(null)
  const startTimeRef = useRef(null)

  const [initialImage, setInitialImage] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [croppedImageBase64, setCroppedImageBase64] = useState(null)
  const [imageBase64, setImageBase64] = useState(null)
  const [filename, setFilename] = useState(null)
  const [cropper, setCropper] = useState(null)

  const [transcription, setTranscription] = useState("")
  const [transcriptionGenLoading, setTranscriptionGenLoading] = useState(false)

  const [imagePrompt, setImagePrompt] = useState("")
  const [generatedImageBase64, setGeneratedImageBase64] = useState(null)
  const [promptGenLoading, setPromptGenLoading] = useState(false)
  const [imageGenLoading, setImageGenLoading] = useState(false)
  const [imageGenElapsed, setImageGenElapsed] = useState(0)
  const [imageGenRequestTime, setImageGenRequestTime] = useState(null)
  const imageGenStartRef = useRef(null)

  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0) // Make sure to be on top of the page
    rpc.post(
      "",
      buildRequest("admin.vocabulary.voices", {})
    ).then(response => {
      if (!response.data.result.message) {
        setVoices(response.data.result.voices)
        setVoice(response.data.result.default)
        if (response.data.result.synt_timeout) {
          setSyntTimeout(response.data.result.synt_timeout)
        }
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        console.error(response.data.result)
      }
    }).catch(err => {
      console.error("Failed to load voices:", err.message)
    })

    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.words.get",
        {
          word_id: wordId,
        }
      )
    ).then((response) => {
      if (!response.data.result.message) {
        setWord(response.data.result.word)
        setTranslation(response.data.result.translation)
        setInitialImage(response.data.result.image)
        setAudioUrl(response.data.result.audio)
        // Audio is synthesized by a background thread after create (#9), so a
        // just-created word can arrive here without it yet — poll until it lands.
        // Gated on awaitAudio so an existing audio-less word doesn't poll/mislead.
        if (awaitAudio && !response.data.result.audio) {
          setAudioPending(true)
        }
        setImagePrompt(response.data.result.image_prompt || "")
        setTranscription(response.data.result.transcription || "")
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message)
      }
    }).catch((error) => {
      setErrorMessage(error.message)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!mp3Loading) return
    const timer = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(timer)
  }, [mp3Loading])

  // Poll for the background-synthesized audio (#9) until it lands or we give up.
  // Bounded so a word whose TTS failed/never runs doesn't poll forever; the
  // manual "Synthesize new sound" control stays available regardless.
  useEffect(() => {
    if (!audioPending) return
    let tries = 0
    const timer = setInterval(() => {
      tries += 1
      rpc.post(
        "",
        buildRequest("admin.vocabulary.words.get", { word_id: wordId })
      ).then(response => {
        const audio = response.data.result && response.data.result.audio
        if (audio) {
          setAudioUrl(audio)
          setAudioPending(false)
        } else if (tries >= 10) {
          setAudioPending(false)
        }
      }).catch(() => {
        if (tries >= 10) setAudioPending(false)
      })
    }, 3000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioPending])

  useEffect(() => {
    if (!imageGenLoading) return
    const timer = setInterval(() => setImageGenElapsed(e => e + 1), 1000)
    return () => clearInterval(timer)
  }, [imageGenLoading])

  const handleVoiceChange = (event) => {
    setVoice(event.target.value)
  };

  const getImageUrl = (e) => {
    if (e.target.files) {
      // Encode the file using the FileReader API
      const reader = new FileReader()
      reader.onloadend = () => {
        setImageBase64(reader.result)
      }
      reader.readAsDataURL(e.target.files[0])
      setFilename(`${uuid()}.${e.target.files[0].name.split('.').pop()}`)
      setImageUrl(URL.createObjectURL(e.target.files[0]))
    }
  }

  const getCropData = async () => {
    if (cropper) {
      setCroppedImageBase64(cropper.getCroppedCanvas().toDataURL())
    }
  }

  const submitForm = (e) => {
    e.preventDefault()

    if ([
      word.length > 0,
      translation.length > 0,
    ].some(x => x === false)) {
      return false
    }

    let image = null
    let newAudio = null

    if (croppedImageBase64) {
      image = {
        base64: croppedImageBase64,
        name: filename,
      }
    } else if (imageUrl) {
      image = {
        base64: imageBase64,
        name: filename,
      }
    } else if (generatedImageBase64) {
      image = {
        base64: `data:image/png;base64,${generatedImageBase64}`,
        name: `${uuid()}.png`,
      }
    }

    if (mp3) {
      newAudio = `data:audio/mp3;base64,${mp3}`
    }

    setErrorMessage(null)
    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.words.update",
        {
          word_id: wordId,
          word,
          translation,
          image,
          audio: newAudio,
          image_prompt: imagePrompt || null,
          transcription: transcription || null,
        },
      )
    ).then(response => {
      if (!response.data.result.message) {
        setWord(response.data.result.word)
        setTranslation(response.data.result.translation)
        setInitialImage(response.data.result.image)
        setAudioUrl(response.data.result.audio)
        setImagePrompt(response.data.result.image_prompt || "")
        setTranscription(response.data.result.transcription || "")
        setImageUrl(null)
        setImageBase64(null)
        setCroppedImageBase64(null)
        setFilename(null)
        setCropper(null)
        setMp3(null)
        setGeneratedImageBase64(null)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message)
      }
    }).catch(err => {
      setErrorMessage("Failed to save: " + err.message)
    })
    return false
  }

  const generateSound = (e) => {
    e.preventDefault()
    setErrorMessage(null)
    setMp3Loading(true)
    setElapsed(0)
    setRequestTime(null)
    startTimeRef.current = performance.now()
    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.synt",
        {
          text: word,
          voice,
        },
      )
    ).then(response => {
      if (!response.data.result.message) {
        if (response.data.result.mp3_base64) {
          setMp3(response.data.result.mp3_base64)
        } else {
          setErrorMessage("missing mp3_base64 in response")
        }
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message || JSON.stringify(response.data.result))
      }
    }).catch(err => {
      setErrorMessage("Failed to synthesize audio: " + err.message)
    }).finally(() => {
      setMp3Loading(false)
      if (startTimeRef.current) {
        const duration = (performance.now() - startTimeRef.current) / 1000
        setRequestTime(duration)
        startTimeRef.current = null
      }
    })
    return false
  }

  const progressValue = Math.min((elapsed / syntTimeout) * 100, 100)
  const progressColor = progressValue > 75 ? 'warning' : 'primary'

  const imageGenTimeout = 60
  const imageGenProgressValue = Math.min((imageGenElapsed / imageGenTimeout) * 100, 100)
  const imageGenProgressColor = imageGenProgressValue > 75 ? 'warning' : 'primary'

  const generatePrompt = (e) => {
    e.preventDefault()
    setErrorMessage(null)
    setPromptGenLoading(true)
    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.words.generate_prompt",
        { word_id: wordId },
      )
    ).then(response => {
      if (!response.data.result.message) {
        setImagePrompt(response.data.result.image_prompt)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message || JSON.stringify(response.data.result))
      }
    }).catch(err => {
      setErrorMessage("Failed to generate prompt: " + err.message)
    }).finally(() => {
      setPromptGenLoading(false)
    })
    return false
  }

  const generateImage = (e) => {
    e.preventDefault()
    setErrorMessage(null)
    setImageGenLoading(true)
    setImageGenElapsed(0)
    setImageGenRequestTime(null)
    imageGenStartRef.current = performance.now()
    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.words.generate_image",
        {
          word_id: wordId,
          prompt: imagePrompt || undefined,
        },
      )
    ).then(response => {
      if (!response.data.result.message) {
        setGeneratedImageBase64(response.data.result.image_base64)
        setImagePrompt(response.data.result.image_prompt)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message || JSON.stringify(response.data.result))
      }
    }).catch(err => {
      setErrorMessage("Failed to generate image: " + err.message)
    }).finally(() => {
      setImageGenLoading(false)
      if (imageGenStartRef.current) {
        const duration = (performance.now() - imageGenStartRef.current) / 1000
        setImageGenRequestTime(duration)
        imageGenStartRef.current = null
      }
    })
    return false
  }

  const varyPrompt = (e) => {
    e.preventDefault()
    setErrorMessage(null)
    setPromptGenLoading(true)
    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.words.vary_prompt",
        { prompt: imagePrompt },
      )
    ).then(response => {
      if (!response.data.result.message) {
        setImagePrompt(response.data.result.image_prompt)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message || JSON.stringify(response.data.result))
      }
    }).catch(err => {
      setErrorMessage("Failed to vary prompt: " + err.message)
    }).finally(() => {
      setPromptGenLoading(false)
    })
    return false
  }

  const deleteWord = () => {
    setErrorMessage(null)
    confirm({
      description: 'This action is permanent. Are you sure you want to delete this word?',
      title: 'Delete word?'
    }).then(() => {
      rpc.post(
        "",
        buildRequest("admin.vocabulary.words.delete", {
          word_id: wordId,
        })
      )
        .then(({ data }) => {
          if (!data.result.code) {
            enqueueSnackbar("Word deleted", { variant: "success" })
            navigate('/words/1')
          } else {
            if (data.result.code === 5) {
              signoutAndRedirectToIndex()
            }
            setErrorMessage(data.result.message)
          }
        })
        .catch(err => {
          setErrorMessage("Failed to delete: " + err.message)
        })
    }).catch(() => {
      // user cancelled
    })
  }

  return (
    <>
      <Helmet>
        <title>Word {word ? `${word}` : 'loading...'}</title>
      </Helmet>
      <Fab
        color="primary"
        aria-label="save"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        disabled={!word || [
          word.length > 0,
          translation.length > 0,
        ].some(x => x === false)}
        onClick={submitForm}
      >
        <SaveIcon />
      </Fab>

      <Container fixed>
        <form onSubmit={submitForm} method="post">
          <Box sx={{ flexGrow: 1, marginTop: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography variant="h4" component="h1" gutterBottom>
                    Word {word ? `${word}` : 'loading...'}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    onClick={deleteWord}
                  >Delete word</Button>
                </Stack>
              </Grid>
              <Grid item xs={8}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Paper>
                      {word && (
                        <Stack padding={1} spacing={1}>
                          <TextField
                            label="Word"
                            error={word.length < 1}
                            helperText={word.length > 0 ? "" : "Word is required"}
                            value={word}
                            onChange={(e) => setWord(e.target.value)}
                          />

                          <TextField
                            label="Translation"
                            error={translation.length < 1}
                            helperText={translation.length > 0 ? "" : "Translation is required"}
                            value={translation}
                            onChange={(e) => setTranslation(e.target.value)}
                          />

                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              label="IPA Transcription"
                              fullWidth
                              value={transcription}
                              onChange={(e) => setTranscription(e.target.value)}
                              placeholder="/həˈloʊ/"
                            />
                            <Button
                              variant="outlined"
                              disabled={transcriptionGenLoading}
                              startIcon={transcriptionGenLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                              onClick={(e) => {
                                e.preventDefault()
                                setTranscriptionGenLoading(true)
                                setErrorMessage(null)
                                rpc.post(
                                  "",
                                  buildRequest(
                                    "admin.vocabulary.words.generate_transcription",
                                    { word_id: wordId },
                                  )
                                ).then(response => {
                                  if (!response.data.result.message) {
                                    setTranscription(response.data.result.transcription)
                                  } else {
                                    if (response.data.result.code === 5) {
                                      signoutAndRedirectToIndex()
                                    }
                                    setErrorMessage(response.data.result.message)
                                  }
                                }).catch(err => {
                                  setErrorMessage("Failed to generate transcription: " + err.message)
                                }).finally(() => {
                                  setTranscriptionGenLoading(false)
                                })
                              }}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Generate
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                    </Paper>
                  </Grid>
                  {errorMessage &&
                    <Grid item xs={12}>
                      <Paper>
                        <Alert severity="error">{errorMessage}</Alert>
                      </Paper>
                    </Grid>
                  }
                  {/* Always rendered. This block used to be gated on
                      `(audioUrl || audioPending)` — so the voice picker and the
                      "Synthesize new sound" button appeared only for words that
                      ALREADY had audio, or that you had just created and were
                      still polling for. A word whose background synthesis failed
                      (Piper down at creation; it never retries) therefore showed
                      no audio UI at all, and could not be recovered from the
                      admin. The recovery control was hidden from exactly the
                      words needing recovery. */}
                  <Grid item xs={12}>
                      <Paper sx={{ padding: 2 }}>
                        <Typography variant="overline">Sound</Typography>

                        {mp3 ? (
                          <Stack spacing={0.5} sx={{ mb: 2 }}>
                            <Typography variant="overline" sx={{ color: 'red' }}>
                              Generated sound (unsaved)
                            </Typography>
                            <audio controls src={`data:audio/mp3;base64,${mp3}`} style={{ display: 'block', maxWidth: 400 }} />
                          </Stack>
                        ) : audioUrl ? (
                          <Stack spacing={0.5} sx={{ mb: 2 }}>
                            <Typography variant="overline" sx={{ color: 'green' }}>
                              Current sound
                            </Typography>
                            <audio controls src={audioUrl} style={{ display: 'block', maxWidth: 400 }} />
                          </Stack>
                        ) : audioPending ? (
                          <Stack spacing={0.5} sx={{ mb: 2, maxWidth: 400 }}>
                            <Typography variant="overline" sx={{ color: 'orange' }}>
                              Generating audio…
                            </Typography>
                            <LinearProgress />
                            <Typography variant="body2" color="text.secondary">
                              Audio is synthesized in the background after creation and will
                              appear here shortly. You can also synthesize it manually below.
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack spacing={0.5} sx={{ mb: 2, maxWidth: 400 }}>
                            <Typography variant="overline" sx={{ color: 'orange' }}>
                              No sound yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              This word has no audio. Background synthesis only runs once,
                              when the word is created, so a word added while the TTS service
                              was unavailable stays silent until it is synthesized here.
                              Pick a voice, synthesize, then <strong>save</strong> — the
                              generated sound is not stored until you do.
                            </Typography>
                          </Stack>
                        )}

                        <Stack spacing={1} sx={{ maxWidth: 400 }}>
                          <Select value={voice} label="Voice" onChange={handleVoiceChange}>
                            {voices.map((v) => (
                              <MenuItem key={v} value={v}>{v}</MenuItem>
                            ))}
                          </Select>

                          <Box>
                            <Button
                              disabled={mp3Loading}
                              variant="contained"
                              onClick={generateSound}
                            >
                              {mp3Loading
                                ? <CircularProgress size={24} color="inherit" />
                                : 'Synthesize new sound'}
                            </Button>
                            {requestTime !== null && !mp3Loading && (
                              <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                                Completed in {requestTime.toFixed(1)}s
                              </Typography>
                            )}
                          </Box>

                          {mp3Loading && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {elapsed}s / {syntTimeout}s
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={progressValue}
                                color={progressColor}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          )}

                          {mp3 && (
                            <Button variant="text" color="error" onClick={() => setMp3(null)}>
                              Remove generated sound
                            </Button>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  {(imageUrl && !croppedImageBase64) && (
                    <Grid item xs={12}>
                      <Paper>
                        <Stack padding={1} spacing={1}>
                          <Cropper
                            src={imageUrl}
                            style={{ height: 400, width: 400 }}
                            aspectRatio={262 / 148}
                            minCropBoxWidth={262}
                            minCropBoxHeight={148}
                            guides={false}
                            checkOrientation={false}
                            onInitialized={(instance) => {
                              setCropper(instance);
                            }}
                          />
                          <Button variant="contained" onClick={getCropData}>
                            <TransformIcon /> Crop Image
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Paper>
                  <Stack padding={1} spacing={1}>
                    <Button
                      variant="contained"
                      component="label"
                    >
                      Upload image
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                        hidden
                        onChange={getImageUrl}
                      />
                    </Button>

                    {imageUrl ? (
                      <>
                        <img src={croppedImageBase64 || imageUrl} alt={word} />
                        <Button
                          variant="contained"
                          disabled={!croppedImageBase64}
                          onClick={() => setCroppedImageBase64(null)}
                        >
                          <UndoIcon /> Recrop Image
                        </Button>
                      </>
                    ) : generatedImageBase64 ? (
                      <>
                        <Typography variant="overline" sx={{ color: 'red' }}>
                          Generated image (unsaved)
                        </Typography>
                        <img src={`data:image/png;base64,${generatedImageBase64}`} alt={word} />
                        <Button variant="text" color="error" onClick={() => setGeneratedImageBase64(null)}>
                          Discard
                        </Button>
                      </>
                    ) : (
                      <img src={initialImage} alt={word} />
                    )}

                  </Stack>
                </Paper>

                <Paper sx={{ mt: 1 }}>
                  <Stack padding={1} spacing={1}>
                    <Typography variant="overline">AI Image Generator</Typography>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">Image Prompt</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          multiline
                          minRows={3}
                          maxRows={8}
                          fullWidth
                          size="small"
                          placeholder="Leave empty to auto-generate from word..."
                          value={imagePrompt}
                          onChange={(e) => setImagePrompt(e.target.value)}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            disabled={promptGenLoading || imageGenLoading}
                            startIcon={promptGenLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                            onClick={generatePrompt}
                          >
                            {promptGenLoading ? 'Generating...' : 'Generate Prompt'}
                          </Button>
                          <Button
                            size="small"
                            disabled={promptGenLoading || imageGenLoading || !imagePrompt}
                            startIcon={promptGenLoading ? <CircularProgress size={16} color="inherit" /> : <CasinoIcon />}
                            onClick={varyPrompt}
                          >
                            Vary
                          </Button>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>

                    <Box>
                      <Button
                        disabled={imageGenLoading}
                        variant="contained"
                        startIcon={imageGenLoading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                        onClick={generateImage}
                      >
                        {imageGenLoading ? 'Generating...' : 'Generate Image'}
                      </Button>
                      {imageGenRequestTime !== null && !imageGenLoading && (
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                          Completed in {imageGenRequestTime.toFixed(1)}s
                        </Typography>
                      )}
                    </Box>

                    {imageGenLoading && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {imageGenElapsed}s / {imageGenTimeout}s
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={imageGenProgressValue}
                          color={imageGenProgressColor}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </form>
      </Container>
    </>
  )
}
